import {
  LegalLayout, Eyebrow, Title, Updated, H2, H3, P, UL, LI,
  Callout, Contact, BackLink, LegalTable,
} from "../sitechrome";

export default function Privacy() {
  return (
    <LegalLayout>
      <Eyebrow>Juridiskt</Eyebrow>
      <Title>Privacy Policy</Title>
      <Updated>Last updated: 7 JULY 2026 &nbsp;·&nbsp; Effective date: 7 JULY 2026</Updated>

      <H2 num="1.">Data we collect</H2>
      <P>We collect the following categories of personal data:</P>

      <H3>Account data</H3>
      <P>
        When you sign in with Apple or Google, we receive a unique identifier from that provider
        and, where available, your name and email address. If you use Sign in with Apple&rsquo;s
        private-relay option, we receive a relay email rather than your real address. We also store
        your chosen username, language preference, and role.
      </P>

      <H3>Learning and usage data</H3>
      <P>
        To provide and track your studies we store your progress — completed chapters, quiz and
        mock-exam results, experience points (XP), streaks, your daily goal, onboarding
        preferences (such as your study timeline and intensity), and the readiness metrics derived
        from your activity.
      </P>

      <H3>Subscription and transaction data</H3>
      <P>
        When you purchase a subscription, we receive information about it from the app stores and
        our subscription provider — including subscription status, product, price, currency,
        purchase, renewal and expiry dates, and transaction identifiers. We <strong>do not</strong>{" "}
        receive or store your full card or payment-account details; payment is handled by Apple or
        Google.
      </P>

      <H3>Technical data</H3>
      <P>
        We may process limited technical information such as device type, operating system, app
        version, and data needed for security, diagnostics, and troubleshooting.
      </P>

      <H3>Communications</H3>
      <P>If you contact us (for example by email), we process the information you provide.</P>

      <H2 num="2.">How we use your data and our legal bases</H2>
      <LegalTable
        head={["Purpose", "Legal basis (GDPR Art. 6)"]}
        rows={[
          ["Create and manage your account; provide the app, content, and progress tracking", "Performance of a contract (Art. 6(1)(b))"],
          ["Process and manage your subscription and access rights", "Performance of a contract (Art. 6(1)(b))"],
          ["Keep accounting and transaction records", "Legal obligation (Art. 6(1)(c)) — Swedish Accounting Act"],
          ["Secure the Service, prevent abuse, and diagnose problems", "Legitimate interests (Art. 6(1)(f))"],
          ["Send push notifications or marketing (where applicable)", "Consent (Art. 6(1)(a))"],
          ["Respond to support requests", "Legitimate interests / contract (Art. 6(1)(f)/(b))"],
        ]}
      />
      <P>
        Where we rely on consent, you may withdraw it at any time without affecting the lawfulness
        of processing before withdrawal.
      </P>

      <H2 num="3.">How your readiness score works</H2>
      <Callout>
        <P>
          The readiness indicator is generated automatically by analysing your in-app performance
          (such as quiz and mock-exam results). This is a study aid only — it produces{" "}
          <strong>no legal or similarly significant effect</strong> and is not a decision made
          solely by automated means within the meaning of GDPR Article 22. You remain in full
          control of your studies and of whether to take the official test.
        </P>
      </Callout>

      <H2 num="4.">Who we share data with</H2>
      <P>
        We do not sell your personal data. We share it only with service providers who process it
        on our behalf (processors) or who act as independent controllers for their own part of the
        flow:
      </P>
      <UL>
        <LI><strong>Apple</strong> — Sign in with Apple and App Store billing;</LI>
        <LI><strong>Google</strong> — Google Sign-In and Google Play billing;</LI>
        <LI><strong>RevenueCat</strong> — subscription management and receipt validation;</LI>
      </UL>
      <P>We may also disclose data where required by law or to protect our legal rights.</P>

      <H2 num="5.">International transfers</H2>
      <P>
        Some of our providers (including Apple, Google, and RevenueCat) may process data outside
        the EU/EEA, such as in the United States. Where this happens, transfers are safeguarded by
        an EU adequacy decision (for example the EU–US Data Privacy Framework) and/or the European
        Commission&rsquo;s Standard Contractual Clauses.
      </P>

      <H2 num="6.">How long we keep your data</H2>
      <UL>
        <LI>
          <strong>Account and learning data</strong> — for as long as your account is active, and
          deleted within [30] days after you delete your account, unless we must keep it longer by
          law.
        </LI>
        <LI>
          <strong>Transaction and accounting records</strong> — retained for up to seven (7) years
          as required by the Swedish Accounting Act (Bokföringslagen).
        </LI>
        <LI>
          <strong>Support communications</strong> — for as long as needed to handle your request
          and a reasonable period thereafter.
        </LI>
      </UL>

      <H2 num="7.">Your rights</H2>
      <P>Under the GDPR you have the right to:</P>
      <UL>
        <LI>access the personal data we hold about you;</LI>
        <LI>have inaccurate data corrected;</LI>
        <LI>have your data erased (&ldquo;right to be forgotten&rdquo;);</LI>
        <LI>restrict or object to certain processing;</LI>
        <LI>receive your data in a portable format;</LI>
        <LI>withdraw consent where processing is based on consent.</LI>
      </UL>
      <P>
        To exercise any of these rights, contact us at the address below. You can also delete your
        account from within the app. We will respond within the time limits required by law.
      </P>

      <H2 num="8.">Complaints</H2>
      <P>
        If you believe we have handled your data unlawfully, you may lodge a complaint with the
        Swedish supervisory authority, the Swedish Authority for Privacy Protection
        (Integritetsskyddsmyndigheten, IMY,{" "}
        <a href="https://www.imy.se" target="_blank" rel="noopener noreferrer">imy.se</a>), or with
        the authority in your country of residence.
      </P>

      <H2 num="9.">Security</H2>
      <P>
        We apply appropriate technical and organisational measures to protect your data against
        unauthorised access, loss, or misuse. No method of transmission or storage is completely
        secure, but we work to protect your information and to limit access to it.
      </P>

      <H2 num="10.">Children</H2>
      <P>
        The Service is intended for users aged 16 and over. We do not knowingly collect personal
        data from children below the age of digital consent without appropriate authorisation. If
        you believe a child has provided us data without proper consent, contact us and we will
        delete it.
      </P>

      <H2 num="11.">Website analytics and cookies</H2>
      <P>
        On teorikorkort.se we use Google Analytics 4 only if you click &ldquo;Accept&rdquo; in the
        cookie banner (legal basis: consent). If you decline or don&rsquo;t choose, no analytics
        cookies are set and no analytics data is sent.
      </P>
      <UL>
        <LI>
          <strong>What is measured</strong> — pages visited, the website language, where the visit
          came from (for example a search engine or a campaign link), the type of device (iOS,
          Android or desktop), starts and results of the free practice test, and clicks on the App
          Store and Google Play buttons. We do not send names, email addresses or other details
          that identify you directly, and the data is not used for advertising.
        </LI>
        <LI>
          <strong>Cookies</strong> — Google Analytics sets the cookies <code>_ga</code> and{" "}
          <code>_ga_&lt;ID&gt;</code> (stored for up to 2 years). Your choice is saved in your
          browser&rsquo;s local storage so we don&rsquo;t ask again.
        </LI>
        <LI>
          <strong>Provider</strong> — Google Ireland Ltd. acts as our processor. Data may be
          transferred to the United States under the EU–US Data Privacy Framework. Analytics data
          is kept for 14 months.
        </LI>
        <LI>
          <strong>Changing your mind</strong> — use &ldquo;Cookie settings&rdquo; in the website
          footer at any time. Declining also removes the Google Analytics cookies.
        </LI>
      </UL>
      <P>
        Links to the App Store and Google Play contain a campaign label (for example
        &ldquo;web_hero&rdquo;) so the stores can report how many downloads came from this
        website. These labels do not identify you.
      </P>

      <H2 num="12.">Changes to this policy</H2>
      <P>
        We may update this Privacy Policy from time to time. If we make material changes, we will
        provide reasonable notice (for example, in the app or by updating the date above). Your
        continued use of the Service after changes take effect constitutes acceptance of the
        updated policy.
      </P>

      <Contact
        title="Data controller & contact"
        lines={[
          "Teorikörkort.se",
          <>Email: <a href="mailto:support@teorikorkort.se">support@teorikorkort.se</a></>,
        ]}
      />

      <BackLink href="/sv/">← Till startsidan</BackLink>
    </LegalLayout>
  );
}